import {Text} from 'platform/foundation';
import styled, {css} from 'styled-components';
import {match} from 'ts-pattern';

import {KeyboardEvent} from 'react';

import {isNil} from 'ramda';

import {suffixTestId, TestIdProps} from 'shared/web';

type RadioAppearance = 'pill' | 'compact';

interface RadioButtonGroupProps<T extends string | number> extends TestIdProps {
  label: string;
  value: T;
  options: {value: T; label: string; 'data-testid'?: string}[];
  onChange: (value: T) => void;
  appearance: RadioAppearance;
}

export function RadioButtonGroup<T extends string | number>(props: RadioButtonGroupProps<T>) {
  const selectedIndex = props.options.findIndex((option) => option.value === props.value);
  const tabStopIndex = Math.max(selectedIndex, 0);

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const count = props.options.length;
    const nextIndex = match(event.key)
      .with('ArrowRight', 'ArrowDown', () => (index + 1) % count)
      .with('ArrowLeft', 'ArrowUp', () => (index + count - 1) % count)
      .with('Home', () => 0)
      .with('End', () => count - 1)
      .otherwise(() => null);

    if (isNil(nextIndex)) {
      return;
    }

    event.preventDefault();
    const option = props.options.at(nextIndex);
    if (isNil(option)) {
      return;
    }

    event.currentTarget.parentElement?.querySelectorAll('button').item(nextIndex)?.focus();
    props.onChange(option.value);
  };

  return (
    <RadioTrack
      role="radiogroup"
      aria-label={props.label}
      $appearance={props.appearance}
      data-testid={suffixTestId('radioGroup', props)}
    >
      {props.options.map((option, index) => (
        <RadioButton
          key={option.value}
          type="button"
          role="radio"
          aria-checked={option.value === props.value}
          tabIndex={index === tabStopIndex ? 0 : -1}
          $appearance={props.appearance}
          $selected={option.value === props.value}
          onClick={() => props.onChange(option.value)}
          onKeyDown={(event) => onKeyDown(event, index)}
          data-testid={option['data-testid'] ?? suffixTestId(`radioItem-${option.value}`, props)}
        >
          <Text
            size={props.appearance === 'pill' ? 'base' : 'small'}
            alternative
            color={option.value === props.value ? 'inverted' : 'secondary'}
          >
            {option.label}
          </Text>
        </RadioButton>
      ))}
    </RadioTrack>
  );
}

const RadioTrack = styled.div<{$appearance: RadioAppearance}>`
  ${({theme, $appearance}) =>
    $appearance === 'pill'
      ? css`
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(${theme.getSize(32)}, 1fr));
          padding: ${theme.getSize(1)};
          background: ${theme.colors.accent.lightest};
          border-radius: ${theme.radii.medium};
        `
      : css`
          display: flex;
          flex-wrap: wrap;
          gap: ${theme.getSize(2)};
        `}
`;

const RadioButton = styled.button<{$appearance: RadioAppearance; $selected: boolean}>`
  ${({theme, $appearance, $selected}) =>
    $appearance === 'pill'
      ? css`
          border: 0;
          border-radius: calc(${theme.radii.medium} - ${theme.getSize(1)});
          padding: ${theme.getSize(3)};
          background: ${$selected ? theme.colors.accent.base : theme.colors.general.transparent};
        `
      : css`
          border: thin solid
            ${$selected ? theme.colors.general.transparent : theme.colors.border.emphasisDefault};
          border-radius: ${theme.radii.large};
          padding: 0 ${theme.getSize(3)};
          min-height: ${theme.getSize(6)};
          background: ${$selected ? theme.colors.accent.base : theme.colors.fill.white};
        `}
  cursor: pointer;
  user-select: none;

  &:focus-visible {
    outline: ${({theme}) => `calc(${theme.getSize(1)} / 2) solid ${theme.colors.accent.dark}`};
    outline-offset: ${({theme}) => theme.getSize(1)};
  }
`;
